<script lang="ts">
  import type {
    ChatStatus,
    Config,
    RateLimitConfig,
  } from '@pin/schemas/common';
  import { RATE_LIMIT_LEVELS } from '@pin/schemas/constants';
  import { untrack } from 'svelte';
  import { updateConfig } from '../../api/api';
  import { adminName } from '../../stores/configStore';
  import { addToast } from '../../stores/toastStore';
  import Modal from '../Modal.svelte';

  interface Props {
    config: Config;
  }

  let { config }: Props = $props();

  const userLimitLabel = ({ maxPoints, duration }: RateLimitConfig['user']) =>
    `${maxPoints} per ${duration} s`;

  const perMinute = ({ maxPoints, duration }: RateLimitConfig['user']) =>
    (maxPoints * 60) / duration;

  // Every level auto moves between, and 2 per 60 s, the runbook's start for
  // a big show, from loosest to strictest. Named by their numbers, so the
  // dropdown says what each one lets through.
  const rateLimitPresets: Record<string, RateLimitConfig> = Object.fromEntries(
    [...RATE_LIMIT_LEVELS, { maxPoints: 2, duration: 60 }]
      .sort((a, b) => perMinute(b) - perMinute(a) || b.maxPoints - a.maxPoints)
      .map((user) => [userLimitLabel(user), { user }])
  );

  // What the old global limit let through on 1 per 60 s, 5 messages
  // every 3 seconds.
  const defaultAutoMessagesPerMinute = 100;

  const chatStatusOptions: { value: ChatStatus; label: string }[] = [
    { value: 'open', label: 'Åpen' },
    { value: 'closed', label: 'Lukket' },
    { value: 'broken', label: 'Ute av drift' },
  ];

  // Local form state seeded from the server config. The effects below reset
  // it when the server values change, so the initial reads are untracked on
  // purpose.
  let selectedRateLimitPreset = $state(
    untrack(() =>
      findRateLimitPresetByConfig(rateLimitPresets, config.rateLimit)
    )
  );
  let customUserLimit = $state<RateLimitConfig['user']>(
    untrack(() => $state.snapshot(config.rateLimit.user))
  );
  let autoMessagesPerMinute = $state(
    untrack(
      () =>
        config.rateLimit.auto?.messagesPerMinute ?? defaultAutoMessagesPerMinute
    )
  );
  let showChatStatusModal = $state(false);
  let chatStatus = $state<ChatStatus>(untrack(() => config.chat.status));

  const handleConfigUpdate = async () => {
    try {
      // Auto starts from the limit in force; admin-api moves it from there
      const rateLimit =
        selectedRateLimitPreset === 'auto'
          ? {
              user: $state.snapshot(config.rateLimit.user),
              auto: { messagesPerMinute: autoMessagesPerMinute },
            }
          : selectedRateLimitPreset === 'custom'
            ? { user: customUserLimit }
            : rateLimitPresets[selectedRateLimitPreset];
      // Our own choice: the CONFIG it comes back as must not reset the
      // form, or leaving auto at a preset's numbers would select the preset
      // instead of custom.
      previousSetting = rateLimitSetting(rateLimit);
      await updateConfig({
        chat: { status: chatStatus },
        rateLimit,
      });
      addToast({ message: 'config updated.' });
    } catch (e) {
      addToast({
        message: `Couldn't update config: ${String(e)}`,
        type: 'error',
      });
    }
  };

  function findRateLimitPresetByConfig<T extends RateLimitConfig>(
    presets: Record<string, T>,
    conf: T
  ) {
    if (conf.auto != null) {
      return 'auto';
    }
    return (
      Object.entries(presets).find(
        ([, v]) =>
          v.user.duration === conf.user.duration &&
          v.user.maxPoints === conf.user.maxPoints
      )?.[0] ?? 'custom'
    );
  }

  // What a moderator chose, as opposed to where auto has moved the limit
  // to. Only a choice made elsewhere resets the form, so auto moving the
  // limit every half minute does not undo what a moderator is typing.
  function rateLimitSetting(conf: RateLimitConfig) {
    if (conf.auto != null) {
      return `auto ${conf.auto.messagesPerMinute}`;
    }
    return `${conf.user.maxPoints} ${conf.user.duration}`;
  }

  let previousSetting = untrack(() => rateLimitSetting(config.rateLimit));
  $effect(() => {
    const setting = rateLimitSetting(config.rateLimit);
    const user = $state.snapshot(config.rateLimit.user);
    if (previousSetting !== setting) {
      selectedRateLimitPreset = findRateLimitPresetByConfig(
        rateLimitPresets,
        config.rateLimit
      );
      customUserLimit = user;
      autoMessagesPerMinute =
        config.rateLimit.auto?.messagesPerMinute ??
        defaultAutoMessagesPerMinute;
      previousSetting = setting;
    } else if (untrack(() => selectedRateLimitPreset) !== 'custom') {
      // While hidden, the custom fields follow the limit in force, whether
      // auto moved it or this moderator picked a preset, so switching to
      // custom starts from there.
      customUserLimit = user;
    }
  });

  let previousStatus = untrack(() => config.chat.status);
  $effect(() => {
    if (previousStatus !== config.chat.status) {
      chatStatus = $state.snapshot(config.chat.status);
      previousStatus = config.chat.status;
    }
  });
</script>

<div class="control-panel">
  <div class="control">
    <label for="adminName">Admin Name</label>
    <input
      id="adminName"
      type="text"
      bind:value={$adminName}
      placeholder="Enter name"
    />
  </div>

  <div class="control">
    <button
      class="chat-status-label"
      onclick={() => (showChatStatusModal = true)}
    >
      <span>Chat status</span>
      <span>{chatStatusOptions.find((o) => o.value === chatStatus)?.label}</span
      >
    </button>
    <Modal
      bind:showModal={showChatStatusModal}
      onclose={() => {
        chatStatus = $state.snapshot(config.chat.status);
      }}
    >
      <h3>Chat status</h3>
      <div class="chat-status-options">
        {#each chatStatusOptions as option (option.value)}
          <button
            class="btn"
            class:selected={chatStatus === option.value}
            onclick={() => (chatStatus = option.value)}
          >
            {option.label}
          </button>
        {/each}
      </div>
      <div class="chat-status-actions">
        <button class="btn" onclick={() => (showChatStatusModal = false)}>
          Cancel
        </button>
        <button
          class="btn"
          onclick={async () => {
            await handleConfigUpdate();
            showChatStatusModal = false;
          }}
        >
          Save
        </button>
      </div>
    </Modal>
  </div>

  <div class="control">
    <label for="rateLimitPreset">Rate limit per user</label>
    <select
      id="rateLimitPreset"
      bind:value={selectedRateLimitPreset}
      onchange={handleConfigUpdate}
    >
      {#each [...Object.keys(rateLimitPresets), 'auto', 'custom'] as preset, index (index)}
        <option value={preset}>{preset}</option>
      {/each}
    </select>
  </div>

  {#if selectedRateLimitPreset === 'auto'}
    <div class="control">
      <label for="autoMessagesPerMinute">Target messages per minute</label>
      <input
        id="autoMessagesPerMinute"
        type="number"
        min="1"
        bind:value={autoMessagesPerMinute}
        placeholder="Messages per minute"
      />
      <p>Now {userLimitLabel(config.rateLimit.user)} per user</p>
    </div>

    <button
      class="btn right"
      onclick={() => {
        if (
          typeof autoMessagesPerMinute !== 'number' ||
          autoMessagesPerMinute < 1
        ) {
          addToast({
            message: 'The target must be a number of at least 1',
            type: 'error',
          });
          return;
        }
        handleConfigUpdate();
      }}>Save target</button
    >
  {/if}

  {#if selectedRateLimitPreset === 'custom'}
    <div class="control">
      <p>User Rate Limit</p>
      <label for="userMaxPoints">Max points</label>
      <input
        id="userMaxPoints"
        type="number"
        bind:value={customUserLimit.maxPoints}
        placeholder="Max points"
      />

      <label for="userDuration">Duration</label>
      <input
        id="userDuration"
        type="number"
        bind:value={customUserLimit.duration}
        placeholder="Duration (s)"
      />
    </div>

    <button
      class="btn right"
      onclick={() => {
        if (
          typeof customUserLimit.maxPoints !== 'number' ||
          typeof customUserLimit.duration !== 'number'
        ) {
          addToast({
            message: 'Rate limit can only be set to a number',
            type: 'error',
          });
          return;
        }
        handleConfigUpdate();
      }}>Save custom</button
    >
  {/if}
</div>

<style>
  .control-panel {
    display: flex;
    flex-direction: column;
    font-size: 0.95rem;
  }

  .control {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
  }

  h3,
  label {
    font-weight: 500;
    margin-bottom: 0.25rem;
  }

  p {
    margin: 0;
    padding: 0;
  }

  select,
  input[type='number'],
  input[type='text'] {
    min-height: 2.25rem;
    padding-left: 0.5rem;
    border: 1px solid var(--color-zinc-400);
    border-radius: 0.25rem;
    background-color: var(--color-zinc-200);
    color: var(--color-zinc-950);
    margin-bottom: 0.25rem;
  }

  .chat-status-label {
    all: unset;
    display: flex;
    flex-direction: column;
    margin-bottom: 0.25rem;
    gap: 0.25rem;
    cursor: pointer;
  }

  .chat-status-label span:nth-child(1) {
    font-weight: 500;
  }

  .chat-status-label span:nth-child(2) {
    display: flex;
    align-items: center;
    color: var(--color-zinc-950);
    background-color: var(--color-zinc-200);
    padding-left: 0.5rem;
    min-height: 2.25rem;
    border-radius: 0.25rem;
    border: 1px solid var(--color-zinc-400);
  }

  .chat-status-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .chat-status-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    background-color: var(--color-zinc-700);
    border-radius: 0.5rem;
    color: var(--color-zinc-200);
    border: 1px solid var(--color-zinc-600);
    font-size: 1rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    width: max-content;
  }

  .btn.right {
    align-self: flex-end;
  }

  .btn.selected {
    background-color: var(--color-green-700);
    color: white;
    border-color: var(--color-green-700);
  }

  .btn:not(.selected):hover {
    background-color: var(--color-zinc-600);
  }

  .btn:focus {
    outline: none;
    border-color: var(--color-zinc-400);
    border-width: 1px;
    border-style: solid;
    border-radius: 0.5rem;
  }
</style>
